import calculateAverage, { calculateAverageChileanLice } from '@/utils/calculateCageAverages';
import { compareCageNames } from '@/utils/compareCageNames';
import { formatNumberByLocale, formatUniversalUnitByLocale } from '@/utils/localeFormatters';

import type { LocationsIncluded } from '../RegionLiceReportPageContent';

/** What a PDF row reads from a pen or from a location's average; a pen can lack a total. */
interface PdfCageRow {
    name?: string;
    femaleAvg?: number;
    femaleTotal?: number | null;
    movingStageAvg?: number;
    movingStageTotal?: number | null;
    caligusElongatusAvg?: number;
    caligusElongatusTotal?: number | null;
    count?: number;
    livingWeight?: number;
    depthAvg?: number;
}

/**
 * Helper function to format the cage data for the PDF
 */
const formatCage = (
    cage: PdfCageRow,
    locationName: string,
    locale: string,
    reportPeriod: string
) => {
    const dataWithLice = {
        report_period: reportPeriod,
        location_name: locationName,
        pen_name: cage?.name,
        female_avg: formatNumberByLocale(cage?.femaleAvg, locale, 2, ''),
        female_total: formatNumberByLocale(cage?.femaleTotal, locale, 0, ''),
        moving_stage_avg: formatNumberByLocale(cage?.movingStageAvg, locale, 2, ''),
        moving_stage_total: formatNumberByLocale(cage?.movingStageTotal, locale, 0, ''),
        caligus_elongatus_avg: formatNumberByLocale(cage?.caligusElongatusAvg, locale, 2, ''),
        caligus_elongatus_total: formatNumberByLocale(cage?.caligusElongatusTotal, locale, 0, ''),
        count: cage?.count ?? '',
        living_weight: formatUniversalUnitByLocale(cage?.livingWeight, 'g', locale, 0, ''),
        depth_avg: formatNumberByLocale(cage?.depthAvg, locale, 1, ''),
    };

    return { ...dataWithLice };
};

const formatChileanCage = (
    cage: PdfCageRow,
    locationName: string,
    locale: string,
    reportPeriod: string
) => {
    const dataWithLice = {
        report_period: reportPeriod,
        location_name: locationName,
        pen_name: cage?.name,
        ovigerous_female_avg: formatNumberByLocale(cage?.femaleAvg, locale, 2, ''),
        ovigerous_female_total: formatNumberByLocale(cage?.femaleTotal, locale, 0, ''),
        moving_stage_avg: formatNumberByLocale(cage?.movingStageAvg, locale, 2, ''),
        moving_stage_total: formatNumberByLocale(cage?.movingStageTotal, locale, 0, ''),
        count: cage?.count ?? '',
        living_weight: formatUniversalUnitByLocale(cage?.livingWeight, 'g', locale, 0, ''),
        depth_avg: formatNumberByLocale(cage?.depthAvg, locale, 1, ''),
    };

    return { ...dataWithLice };
};

/**
 *
 * @param param0 The Container component preparing the data for the PDF
 */

interface DownloadPDFContainerProps {
    locationsIncluded: LocationsIncluded[];
    /** Human-readable report period (week, day, or date range). */
    reportPeriod: string;
    clientName: string;
    locale?: string;
}

const LocationPreparePDFdata = ({
    locationsIncluded,
    reportPeriod,
    locale = 'en',
}: DownloadPDFContainerProps) => {
    const filteredCagesLocation = locationsIncluded.map((location) => ({
        name: location.name,
        id: location.id,
        // The API returns pens in no set order; sort them as the on-screen table does.
        cages: location.cages
            .filter((cage) => cage.isIncluded)
            .sort((a, b) => compareCageNames(a.name, b.name)),
        isChileanLice: location?.isChileanLice ?? false,
    }));

    const allData =
        filteredCagesLocation?.flatMap((location) => {
            // filteredCagesLocation holds only the included cages.
            const result = location.cages.map((cage) =>
                location.isChileanLice
                    ? formatChileanCage(cage, location.name, locale, reportPeriod)
                    : formatCage(cage, location.name, locale, reportPeriod)
            );
            // Calculate average of each Location
            const average = location.isChileanLice
                ? calculateAverageChileanLice({
                      cages: location.cages.map((cage) => ({
                          ...cage,
                          caligusElongatusAvg: cage.caligusElongatusAvg ?? 0,
                          livingWeight: cage.livingWeight ?? 0,
                          femaleTotal: cage.femaleTotal ?? 0,
                          caligusElongatusTotal: cage.caligusElongatusTotal ?? 0,
                          movingStageTotal: cage.movingStageTotal ?? 0,
                          isIncluded: cage.isIncluded ?? false,
                      })),
                  })
                : calculateAverage({
                      cages: location.cages.map((cage) => ({
                          ...cage,
                          caligusElongatusAvg: cage.caligusElongatusAvg ?? 0,
                          livingWeight: cage.livingWeight ?? 0,
                          femaleTotal: cage.femaleTotal ?? 0,
                          caligusElongatusTotal: cage.caligusElongatusTotal ?? 0,
                          movingStageTotal: cage.movingStageTotal ?? 0,
                          isIncluded: cage.isIncluded ?? false,
                      })),
                  });

            const formattedAverage = location.isChileanLice
                ? formatChileanCage(average, location.name, locale, reportPeriod)
                : formatCage(average, location.name, locale, reportPeriod);

            formattedAverage.count = average.count;

            return [
                ...result,
                formattedAverage,
                location.isChileanLice
                    ? {
                          report_period: '',
                          location_name: '',
                          pen_name: '',
                          ovigerous_female_avg: '',
                          ovigerous_female_total: '0',
                          moving_stage_avg: '',
                          moving_stage_total: '0',
                          count: '',
                          living_weight: '',
                          depth_avg: '',
                      }
                    : {
                          report_period: '',
                          location_name: '',
                          pen_name: '',
                          female_avg: '',
                          female_total: '0',
                          moving_stage_avg: '',
                          moving_stage_total: '0',
                          caligus_elongatus_avg: '',
                          caligus_elongatus_total: '0',
                          count: '',
                          living_weight: '',
                          depth_avg: '',
                      },
            ];
        }) ?? [];

    const dataColumnNames = allData.length > 0 && allData[0] ? Object.keys(allData[0]) : [];

    const formattedDataColumnNames = dataColumnNames.map((e) => `day.${e}`);

    return { allData, formattedDataColumnNames, dataColumnNames };
};

export default LocationPreparePDFdata;
export { LocationPreparePDFdata };
